import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createQuote, Source } from '../lib/api';
import { TagAutocomplete } from './TagAutocomplete';

interface ImagePreview {
  file: File;
  preview: string;
  pageNumber?: string;
}

interface QuoteFormProps {
  sources: Source[];
  onSuccess?: () => void;
}

export const QuoteForm: React.FC<QuoteFormProps> = ({ sources, onSuccess }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [sourceId, setSourceId] = useState<string>('');
  const [sourceSearch, setSourceSearch] = useState('');
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      // Validierung
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" ist kein Bild.`);
        return;
      }

      if (file.size > 50 * 1024 * 1024) {
        setError(`"${file.name}" ist zu groß (max 50MB).`);
        return;
      }

      // Preview erstellen
      const reader = new FileReader();
      reader.onload = (event) => {
        setImages((prev) => [
          ...prev,
          {
            file,
            preview: event.target?.result as string,
            pageNumber: '',
          },
        ]);
      };
      reader.readAsDataURL(file);
    });

    // Input zurücksetzen für erneute Auswahl
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => {
      // Speicher freigeben
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handlePageNumberChange = (index: number, value: string) => {
    setImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, pageNumber: value } : img))
    );
  };

  const handleReorderImages = (fromIndex: number, toIndex: number) => {
    setImages((prev) => {
      const newImages = [...prev];
      const [moved] = newImages.splice(fromIndex, 1);
      newImages.splice(toIndex, 0, moved);
      return newImages;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sourceId) {
      setError('Bitte wähle eine Quelle aus.');
      return;
    }

    if (images.length === 0) {
      setError('Bitte lade mindestens ein Bild hoch.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('source_id', sourceId);
    formData.append('text_content', textContent);

    // Tags als kommagetrennte Liste
    formData.append('tag_names', selectedTags.join(','));

    // Seitenzahlen als JSON-Array, dann Bilder
    // Leere Seitenzahl als null senden — ein Leerstring landet sonst in der DB
    // und lässt das Zitat beim Laden am Response-Schema scheitern.
    formData.append(
      'images_page_numbers',
      JSON.stringify(images.map((img) => (img.pageNumber?.trim() ? img.pageNumber.trim() : null)))
    );
    images.forEach((img) => {
      formData.append('images', img.file);
    });

    try {
      await createQuote(formData);
      onSuccess?.();
      navigate('/');
    } catch (err: unknown) {
      console.error('Fehler beim Erstellen:', err);
      const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
      if (Array.isArray(detail)) {
        setError(detail.map((d: { msg?: string }) => d.msg ?? '').join(', '))
      } else if (typeof detail === 'string') {
        setError(detail)
      } else {
        setError('Fehler beim Speichern. Bitte versuche es erneut.')
      }
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">
          Neues Zitat erstellen
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Quelle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quelle <span className="text-red-500">*</span>
            </label>
            {sources.length === 0 ? (
              <p className="text-sm text-yellow-600 mt-1">
                Noch keine Quellen vorhanden. Bitte erstelle zuerst eine Quelle.
              </p>
            ) : sourceId ? (
              (() => {
                const selected = sources.find((s) => String(s.id) === sourceId);
                return selected ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.625rem 0.875rem',
                    border: '1px solid #d1d5db', borderRadius: '0.375rem',
                    background: '#f9fafb',
                  }}>
                    <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>
                      {selected.type === 'book' ? '📖' : '📄'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {selected.title}
                      </div>
                      {(selected.authors?.length > 0 || selected.year) && (
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          {selected.authors?.map((a) => a.name).join(', ')}
                          {selected.authors?.length > 0 && selected.year && ' · '}
                          {selected.year}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => { setSourceId(''); setSourceSearch(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1.25rem', lineHeight: 1, padding: '0 0.25rem', flexShrink: 0 }}
                      disabled={isSubmitting}
                    >×</button>
                  </div>
                ) : null;
              })()
            ) : (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={sourceSearch}
                  onChange={(e) => { setSourceSearch(e.target.value); setShowSourceDropdown(true); }}
                  onFocus={() => setShowSourceDropdown(true)}
                  onBlur={() => setTimeout(() => setShowSourceDropdown(false), 150)}
                  placeholder="Quelle suchen…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSubmitting}
                />
                {showSourceDropdown && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: 'white', border: '1px solid #d1d5db', borderRadius: '0.375rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '220px', overflowY: 'auto',
                    marginTop: '0.25rem',
                  }}>
                    {sources
                      .filter((s) =>
                        s.title.toLowerCase().includes(sourceSearch.toLowerCase()) ||
                        s.authors?.some((a) => a.name.toLowerCase().includes(sourceSearch.toLowerCase()))
                      )
                      .map((source) => (
                        <div
                          key={source.id}
                          onMouseDown={() => { setSourceId(String(source.id)); setShowSourceDropdown(false); setSourceSearch(''); }}
                          style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.625rem' }}
                          className="hover:bg-gray-100"
                        >
                          <span style={{ flexShrink: 0 }}>{source.type === 'book' ? '📖' : '📄'}</span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{source.title}</div>
                            {(source.authors?.length > 0 || source.year) && (
                              <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                                {source.authors?.map((a) => a.name).join(', ')}
                                {source.authors?.length > 0 && source.year && ' · '}
                                {source.year}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    {sources.filter((s) =>
                      s.title.toLowerCase().includes(sourceSearch.toLowerCase()) ||
                      s.authors?.some((a) => a.name.toLowerCase().includes(sourceSearch.toLowerCase()))
                    ).length === 0 && (
                      <div style={{ padding: '0.5rem 0.75rem', color: '#9ca3af', fontSize: '0.875rem' }}>
                        Keine Ergebnisse
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bild-Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bilder hochladen
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="image-upload"
                disabled={isSubmitting}
              />
              <label
                htmlFor="image-upload"
                className="flex flex-col items-center cursor-pointer"
              >
                <span className="text-4xl mb-2">📷</span>
                <span className="text-gray-600 text-center">
                  Klicke um Bilder auszuwählen
                  <br />
                  <span className="text-sm text-gray-400">
                    (mehrere Bilder möglich, max 10MB pro Bild)
                  </span>
                </span>
              </label>
            </div>
          </div>

          {/* Bild-Previews */}
          {images.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hochgeladene Bilder ({images.length})
              </label>
              <div className="space-y-4">
                {images.map((img, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 bg-gray-50 p-4 rounded-lg"
                  >
                    <img
                      src={img.preview}
                      alt={`Preview ${index + 1}`}
                      className="w-24 h-24 object-cover rounded-md"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 truncate max-w-xs">
                        {img.file.name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {(img.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <div className="mt-2">
                        <label className="text-sm text-gray-600">
                          Seitenzahl:
                          <input
                            type="text"
                            value={img.pageNumber}
                            onChange={(e) =>
                              handlePageNumberChange(index, e.target.value)
                            }
                            placeholder="z.B. 42"
                            className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm w-20"
                          />
                        </label>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            handleReorderImages(index, index - 1)
                          }
                          className="text-gray-500 hover:text-gray-700"
                          title="Nach oben"
                        >
                          ↑
                        </button>
                      )}
                      {index < images.length - 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            handleReorderImages(index, index + 1)
                          }
                          className="text-gray-500 hover:text-gray-700"
                          title="Nach unten"
                        >
                          ↓
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="text-red-500 hover:text-red-700"
                        title="Entfernen"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <TagAutocomplete
              selectedTags={selectedTags}
              onChange={setSelectedTags}
              disabled={isSubmitting}
            />
          </div>

          {/* Text (optional - für Verifizierung/Korrektur) */}
          <div>
            <label
              htmlFor="text"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Text (optional - für Verifizierung)
            </label>
            <textarea
              id="text"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Der erkannte Text wird hier angezeigt oder du kannst ihn manuell eingeben..."
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            />
            <p className="text-sm text-gray-500 mt-1">
              Falls leer, wird der Text automatisch per OCR erkannt.
            </p>
          </div>

          {/* Fehleranzeige */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          {/* Upload Progress */}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors"
              disabled={isSubmitting}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !sourceId || images.length === 0}
              className="flex-1 bg-blue-900 text-white py-2 px-4 rounded-md hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Wird erstellt...' : 'Zitat erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
